import React from 'react'
import { Box, Card, CardContent, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import Image from '../ContentfulEntries/Image'
import Link from '../Link'
import { PostLink } from '../../types/landingPage'
import useUrlPathBuilder from '../../hooks/useUrlPathBuilder'

interface Props {
  link: PostLink
}

const LandingPagePost: React.FC<Props> = ({ link }) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  const { blogPrefix, urlPathBuilder } = useUrlPathBuilder()

  return (
    <Card
      className={classes.postContent}
      elevation={0}
      component={(props) => (
        // @ts-ignore
        <Link to={urlPathBuilder([blogPrefix, link.slug])} {...props} />
      )}
    >
      {link.featuredImage && (
        <Image
          asset={link.featuredImage}
          className={cx(classes.column, classes.img)}
        />
      )}
      <CardContent className={classes.postDetail}>
        <Box className={classes.postSubCategory}>
          {link.categories?.length && (
            <Link
              to={urlPathBuilder([
                blogPrefix,
                'category',
                link.categories[0].slug,
              ])}
              className={classes.category}
            >
              {link.categories[0].name}
            </Link>
          )}
          <Typography variant="caption" className={classes.caption}>
            {t('postHeader.readingTime', {
              minutes: Math.ceil(link.fields?.readingTime.minutes),
            })}
          </Typography>
        </Box>
        <Typography className={classes.title} variant="h5">
          {link.title}
        </Typography>
      </CardContent>
    </Card>
  )
}

const useStyles = makeStyles()((theme) => ({
  postContent: {
    flexGrow: 1,
    minWidth: '20rem',
    flexBasis: '30%',
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginBottom: '2em',

    '&:hover': {
      boxShadow: '0 4px 15px 0 #d3d3d3',
      textDecoration: 'none',
    },

    [theme.breakpoints.down('md')]: {
      flexGrow: 0,
      minWidth: 0,
      flexBasis: '100%',
    },
  },
  postDetail: {
    '&&': {
      padding: '2em 1.5em 2.5em',
    },
  },
  postSubCategory: {
    marginBottom: '.8em',
  },
  category: {
    textTransform: 'uppercase',
    fontWeight: 'bold',
    color: theme.palette.action.active,
    marginRight: '1em',
    fontSize: '0.88em',
  },
  caption: {
    color: theme.palette.text.hint,
    fontWeight: 'bold',
  },
  title: {
    color: theme.palette.text.primary,
  },
  column: {
    height: '100%',
    width: '100%',
  },
  img: {
    '&&': {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius,
      height: 'auto',
      aspectRatio: '5/3',
      maxHeight: 'none !important',
      maxWidth: 'none !important',
    },
  },
}))

export default LandingPagePost
